import React, { useEffect, useState } from 'react'
import { Container, Grid, Card, CardMedia, CardContent, CardActions, Typography, Button, Box, Stack, CircularProgress, useTheme } from '@mui/material'
import { Navbar } from '../features/navigation/components/Navbar'
import { Footer } from '../features/footer/Footer'
import StarIcon from '@mui/icons-material/Star'
import { useNavigate } from 'react-router-dom'
import { axiosi } from '../config/axios'

const getBrandName = (brand) => {
  if (!brand) return ''
  return typeof brand === 'object' ? brand?.name || '' : brand
}

export const RecommendedPerfumesPage = () => {
  const theme = useTheme()
  const navigate = useNavigate()
  const [products, setProducts] = useState([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    fetchRecommendedProducts()
  }, [])

  const fetchRecommendedProducts = async () => {
    try {
      setLoading(true)
      const response = await axiosi.get('/products')
      const allProducts = response.data.data || response.data || []
      
      // Filter products marked as recommended
      const recommended = allProducts
        .filter(p => !p.isDeleted && p.isRecommended === true)
        .slice(0, 12)
      
      setProducts(recommended)
    } catch (error) {
      console.error('Error fetching products:', error)
    } finally {
      setLoading(false)
    }
  }

  const handleBuyNow = (productId) => {
    navigate(`/product-details/${productId}`)
  }

  if (loading) {
    return (
      <Stack minHeight="100vh" sx={{ display: 'flex', flexDirection: 'column' }}>
        <Navbar />
        <Container sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', py: 4 }}>
          <CircularProgress />
        </Container>
        <Footer />
      </Stack>
    )
  }

  return (
    <Stack minHeight="100vh" sx={{ display: 'flex', flexDirection: 'column' }}>
      <Navbar />
      <Container maxWidth="lg" sx={{ flex: 1, py: 4 }}>
        {/* Header Section */}
        <Box sx={{ mb: 4, textAlign: 'center' }}>
          <Typography
            variant="h3"
            sx={{
              fontWeight: 800,
              mb: 2,
              background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark})`,
              backgroundClip: 'text',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
            }}
          >
            ✨ Recommended Perfumes
          </Typography>
          <Typography variant="body1" sx={{ color: 'text.secondary', maxWidth: '600px', mx: 'auto' }}>
            Discover our handpicked collection of premium fragrances, carefully selected for their exceptional quality and captivating aromas.
          </Typography>
        </Box>

        {/* Products Grid */}
        <Grid container spacing={3}>
          {products.map((product) => (
            <Grid item xs={12} sm={6} md={4} lg={3} key={product._id}>
              <Card
                sx={{
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    boxShadow: '0 8px 24px rgba(0,0,0,0.15)',
                    transform: 'translateY(-4px)',
                  },
                  borderRadius: '12px',
                  overflow: 'hidden',
                }}
              >
                {/* Image Section */}
                <Box sx={{ position: 'relative', overflow: 'hidden', height: 250 }}>
                  <CardMedia
                    component="img"
                    height="250"
                    image={product.images?.[0] || '/placeholder.jpg'}
                    alt={product.title}
                    sx={{
                      transition: 'transform 0.3s ease',
                      '&:hover': {
                        transform: 'scale(1.05)',
                      },
                      objectFit: 'cover',
                    }}
                  />
                  {/* Premium Badge */}
                  <Box
                    sx={{
                      position: 'absolute',
                      top: 12,
                      right: 12,
                      backgroundColor: theme.palette.primary.main,
                      color: 'white',
                      padding: '4px 12px',
                      borderRadius: '20px',
                      fontSize: '0.75rem',
                      fontWeight: 600,
                      display: 'flex',
                      alignItems: 'center',
                      gap: 0.5,
                    }}
                  >
                    <StarIcon sx={{ fontSize: '0.9rem' }} />
                    Premium
                  </Box>
                </Box>

                {/* Content Section */}
                <CardContent sx={{ flex: 1, pb: 1 }}>
                  <Typography
                    gutterBottom
                    variant="h6"
                    sx={{
                      fontWeight: 700,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                      mb: 1,
                    }}
                  >
                    {product.title}
                  </Typography>

                  {/* Brand */}
                  {getBrandName(product.brand) && (
                    <Typography variant="body2" sx={{ color: theme.palette.primary.main, fontWeight: 600, mb: 1 }}>
                      {getBrandName(product.brand)}
                    </Typography>
                  )}

                  {/* Description */}
                  <Typography
                    variant="body2"
                    sx={{
                      color: 'text.secondary',
                      display: '-webkit-box',
                      WebkitLineClamp: 2,
                      WebkitBoxOrient: 'vertical',
                      overflow: 'hidden',
                      mb: 2,
                    }}
                  >
                    {product.description || 'Premium fragrance with exquisite notes'}
                  </Typography>

                  {/* Price */}
                  <Typography
                    variant="h6"
                    sx={{
                      fontWeight: 800,
                      color: theme.palette.primary.main,
                      fontSize: '1.3rem',
                    }}
                  >
                    Rs. {product.price}
                  </Typography>
                </CardContent>

                {/* Action Button */}
                <CardActions sx={{ pt: 0 }}>
                  <Button
                    fullWidth
                    variant="contained"
                    size="small"
                    sx={{
                      background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark})`,
                      '&:hover': {
                        boxShadow: '0 4px 12px rgba(0,0,0,0.2)',
                      },
                    }}
                    onClick={() => handleBuyNow(product._id)}
                  >
                    Order Now
                  </Button>
                </CardActions>
              </Card>
            </Grid>
          ))}
        </Grid>

        {products.length === 0 && (
          <Box sx={{ textAlign: 'center', py: 8 }}>
            <Typography variant="h6" sx={{ color: 'text.secondary' }}>
              No recommended perfumes available at the moment.
            </Typography>
          </Box>
        )}
      </Container>
      <Footer />
    </Stack>
  )
}
